import React, { useState } from 'react';
import {
  MarkAsDuplicateFunction,
  Study,
  UndoDuplicateFunction,
} from '../StudyList';
import { markAsDuplicate } from '../markAsDuplicate';
import {
  Button,
  createToast,
  dismissToast,
  Icon,
  IconList,
  Toast,
  ToastButton,
} from 'components/core';

export type DuplicateProps = {
  uniqueIndex: number;
  studyId: Study['id'];
  reviewId: string | null;
  onMarkAsDuplicate: MarkAsDuplicateFunction;
  onUndoDuplicate: UndoDuplicateFunction;
  markAsDuplicateRequest?: (studyId: string) => Promise<string>;
};

export const DuplicateButton = ({
  uniqueIndex,
  studyId,
  reviewId,
  onMarkAsDuplicate,
  onUndoDuplicate,
  markAsDuplicateRequest = markAsDuplicate,
}: DuplicateProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleUndoDuplicate = async () => {
    dismissToast();
    try {
      await onUndoDuplicate(studyId);
    } catch {
      createToast(
        <Toast type="danger" role="alert">
          Oh no! We&apos;re experiencing some technical difficulties right now,
          please try again later
        </Toast>
      );
    }
  };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Read from the event rather than document.activeElement: Safari/WebKit
    // doesn't focus a button on mouse click, so activeElement wouldn't
    // reliably be this button for mouse users.
    const triggerElement = event.currentTarget;

    dismissToast();
    try {
      setIsLoading(true);

      await markAsDuplicateRequest(studyId.toString());

      setIsLoading(false);
      onMarkAsDuplicate(studyId, false);

      createToast(
        <Toast type="success" role="alert" focusOnAppear>
          {`Reference #${uniqueIndex} moved to duplicates`}
          <ToastButton
            to={`/reviews/${reviewId}/duplicates/manual_duplicates?id=${studyId}`}
            aria-label={`View #${uniqueIndex} study`}
          >
            View
          </ToastButton>
          <ToastButton
            onClick={handleUndoDuplicate}
            aria-label={`Undo duplicate merge of #${uniqueIndex} study`}
          >
            Undo
          </ToastButton>
        </Toast>
      );
    } catch (errorMessage) {
      onMarkAsDuplicate(studyId, true);

      createToast(
        <Toast type="danger" role="alert" restoreFocusTo={triggerElement}>
          {errorMessage}
        </Toast>
      );

      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      type="brand"
      size="sm"
      isLoading={isLoading}
      onClick={handleClick}
      data-pendo-key="duplicate-button"
    >
      <Icon icon={IconList.light.faBan} />
      Duplicate
    </Button>
  );
};
